import { loginSchema } from '@discordo/shared';
import { useState, type FormEvent, type ReactElement } from 'react';

import { ApiError } from '../lib/api';
import { useAuth } from '../store/auth';

/**
 * Login screen.
 *
 * The credential is the e-mail, never the nickname — the nickname is editable
 * by its owner, and an identity that moves under the account is not one to
 * authenticate with.
 *
 * The form is validated with `loginSchema` — the same schema the API parses the
 * body with — so the rules and their messages exist in exactly one place and a
 * malformed attempt never leaves the browser. That schema also trims and
 * lowercases the address, so what is sent is already normalized.
 */

const GENERIC_FAILURE = 'Não foi possível entrar. Tente de novo.';

export interface LoginPageProps {
  /**
   * The invite code the visitor arrived with, when they came from
   * `/invite/<code>` without a session.
   *
   * It changes nothing about signing in — it only offers the way out for
   * somebody who has no account yet, since the same code is what opens the
   * registration. Without it the invite would be a dead end for exactly the
   * people it was cut for.
   */
  inviteCode?: string;
}

export function LoginPage({ inviteCode }: LoginPageProps = {}): ReactElement {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Dados inválidos.');
      return;
    }

    setSubmitting(true);
    try {
      await login(parsed.data);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : GENERIC_FAILURE);
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-screen">
      <form
        className="auth-card"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <h1 className="auth-title">Discordo</h1>
        <p className="auth-subtitle">Entre para continuar a conversa.</p>

        {error !== null && (
          <p className="auth-alert" role="alert">
            {error}
          </p>
        )}

        <div className="auth-field">
          <label htmlFor="login-email">E-mail</label>
          <input
            id="login-email"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <div className="auth-field">
          <label htmlFor="login-password">Senha</label>
          <input
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        <button className="auth-submit" type="submit" disabled={submitting}>
          {submitting ? 'Entrando…' : 'Entrar'}
        </button>

        {inviteCode === undefined ? (
          <p className="auth-footnote">
            O cadastro é só por convite. Peça um link a quem já usa o servidor.
          </p>
        ) : (
          <p className="auth-footnote">
            Ainda não tem conta?{' '}
            <a href={`/register?invite=${encodeURIComponent(inviteCode)}`}>
              criar uma conta com este convite
            </a>
          </p>
        )}
      </form>
    </main>
  );
}

export default LoginPage;
